import { Injectable } from '@angular/core';
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable, tap, map } from "rxjs";
import { environment } from '../../../environments/environment';
import { IUser } from "../models/IUser";
import { ApiResponse } from '../models/ApiResponse';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  private _user = new BehaviorSubject<IUser | null>(null);
  user$ = this._user.asObservable();

  constructor(private _http: HttpClient) {
  }

  loadProfile(): Observable<IUser> {
    return this._http.get<ApiResponse<IUser>>(`${environment.apiUrl}/api/users/details`).pipe(
      map(res => res.data),
      tap(user => this._user.next(user))
    );
  }

  get currentUser(): IUser | null {
    return this._user.value;
  }

  clearProfile() {
    this._user.next(null);
  }

  getAllUsers(): Observable<IUser[]> {
    return this._http.get<ApiResponse<IUser[]>>(`${environment.apiUrl}/api/users`).pipe(
      map(res => res.data)
    );
  }

  activateUser(userId: string): Observable<string> {
    return this._http.post<ApiResponse<any>>(`${environment.apiUrl}/api/users/activate/${userId}`, {}).pipe(
      map(res => res.message)
    );
  }

  suspendUser(userId: string): Observable<string> {
    return this._http.post<ApiResponse<any>>(`${environment.apiUrl}/api/users/suspend/${userId}`, {}).pipe(
      map(res => res.message)
    );
  }

  deleteUser(userId: string): Observable<string> {
    return this._http.delete<ApiResponse<any>>(`${environment.apiUrl}/api/users/${userId}`).pipe(
      map(res => res.message)
    );
  }

  addToFavorites(companyId: string): Observable<string> {
    return this._http.post<ApiResponse<any>>(`${environment.apiUrl}/api/users/favorites`, { companyId }).pipe(
      map(res => res.message),
      tap(() => {
        if (this._user.value) {
          const updatedFavorites = [...this._user.value.favorites, companyId];
          this._user.next({ ...this._user.value, favorites: updatedFavorites });
        }
      })
    );
  }

  removeFromFavorites(companyId: string): Observable<string> {
    return this._http.delete<ApiResponse<any>>(`${environment.apiUrl}/api/users/favorites/${companyId}`).pipe(
      map(res => res.message),
      tap(() => {
        if (this._user.value) {
          const updatedFavorites = this._user.value.favorites.filter(id => id !== companyId);
          this._user.next({ ...this._user.value, favorites: updatedFavorites });
        }
      })
    );
  }

  editUser(userId: string, updatedUser: IUser): Observable<IUser> {
    return this._http.patch<ApiResponse<IUser>>(`${environment.apiUrl}/api/users/${userId}`, updatedUser).pipe(
      map(res => res.data),
      tap(user => this._user.next(user))
    );
  }

  uploadAvatar(file: File): Observable<string> {
    const formData = new FormData();
    formData.append('avatar', file);

    return this._http.post<ApiResponse<string>>(`${environment.apiUrl}/api/users/avatar`, formData).pipe(
      map(res => res.data)
    );
  }

  upgradePlan(planName: string): Observable<any> {
    return this._http.post(`${environment.apiUrl}/api/users/upgrade-plan`, { planName });
  }
}
